import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, Package2, Cog, Factory, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ProductManufacturingCard from '../components/products/ProductManufacturingCard';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';

interface ManufacturedProduct {
  id: string;
  user_id: string;
  name: string;
  description: string;
  category_id: string;
  price: number;
  cost_price: number;
  stock_quantity: number;
  min_stock_level: number;
  image: string;
  sku: string;
  location: string;
  status: 'design' | 'prototype' | 'production' | 'discontinued';
  bill_of_materials: any[];
  production_time: number;
  batch_size: number;
  created_at: string;
  updated_at: string;
}

const ProductsManufacturingPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState<ManufacturedProduct[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [lastUpdated] = useState(new Date().toLocaleString());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setStatusFilter(filter);
      setIsFiltersVisible(true);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      // Check if manufactured_products table exists by trying a simple query
      try {
        const { data: productsData, error: productsError } = await supabase
          .from('manufactured_products')
          .select('*', { count: 'exact', head: true });

        if (productsError && productsError.code === '42P01') {
          // Table doesn't exist
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setProducts([]);
        } else if (productsError) {
          throw productsError;
        } else {
          // Table exists, now load the actual data
          const { data: fullData, error: fullError } = await supabase
            .from('manufactured_products')
            .select('*')
            .order('created_at', { ascending: false });

          if (fullError) throw fullError;
          setProducts(fullData || []);
        }
      } catch (tableError: any) {
        if (tableError.code === '42P01') {
          console.log('Manufactured products table not yet created. Please run the migration to create it.');
          setProducts([]);
        } else {
          throw tableError;
        }
      }
      
      // Load categories for filtering
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading manufactured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('manufactured_products')
          .delete()
          .eq('id', id);

        if (error) throw error;
        setProducts(prev => prev.filter(product => product.id !== id));
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || product.category_id === categoryFilter;

    const matchesStatus =
      statusFilter === 'all' ||
      product.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('filter');
      } else {
        params.set('filter', value);
      }
      return params;
    });
  };

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    })),
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'design', label: 'Design Phase' },
    { value: 'prototype', label: 'Prototype' },
    { value: 'production', label: 'In Production' },
    { value: 'discontinued', label: 'Discontinued' },
  ];

  // Calculate summary statistics
  const totalProducts = products.length;
  const inProduction = products.filter(product => product.status === 'production').length;
  const prototypes = products.filter(product => product.status === 'prototype').length;
  const designPhase = products.filter(product => product.status === 'design').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Manufacturing facility with assembly line and finished products"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Products Manufacturing</h1>
              <p className="text-lg opacity-90">
                Create finished products from your supply inventory
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Package2 className="h-4 w-4 mr-2" />
                  <span>{totalProducts} Total Products</span>
                </div>
                <div className="flex items-center">
                  <Factory className="h-4 w-4 mr-2" />
                  <span>{inProduction} In Production</span>
                </div>
                <div className="flex items-center">
                  <Cog className="h-4 w-4 mr-2" />
                  <span>{prototypes} Prototypes</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Link to="/products/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Product
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-full mr-3">
              <Package2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-full mr-3">
              <Factory className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">In Production</p>
              <p className="text-2xl font-bold text-gray-900">{inProduction}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-full mr-3">
              <Cog className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Prototypes</p>
              <p className="text-2xl font-bold text-gray-900">{prototypes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-amber-50 rounded-full mr-3">
              <TrendingUp className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Design Phase</p>
              <p className="text-2xl font-bold text-gray-900">{designPhase}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-600" />
          </div>
          <Input
            type="search"
            placeholder="Search products, SKUs, descriptions..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>
        <Button 
          variant="secondary" 
          className="w-full sm:w-auto"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {isFiltersVisible && (
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Category"
              options={categoryOptions}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              fullWidth
            />
          </div>
        </div>
      )}

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Package2 className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500 mb-4">Create your first manufactured product from supplies</p>
            <Link to="/products/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Product
              </Button>
            </Link>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <ProductManufacturingCard
              key={product.id}
              product={product}
              onDelete={handleDeleteProduct}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsManufacturingPage;